import { memo } from "react";
import { Button, ButtonBorder, ButtonSize, ButtonTheme } from "../button";
import { Checkbox } from "../checkbox";
import { Flex } from "../flex";
import { InputSize } from "../input";
import { ExternalLink } from "../link";
import { OtpInput } from "../otp-input";
import { Header, Text } from "../text";
import { FormTextInput } from "../text-input";
import {
  RedoLoginComponentProps,
  RedoLoginContainer,
  RedoLoginProps,
  RESEND_CODE_DELAY,
} from "./redo-login-container";
import * as redoLoginCss from "./redo-login.module.css";

export const RedoLogin = memo(function RedoLogin(props: RedoLoginProps) {
  return (
    <RedoLoginContainer
      LoginComponent={RedoLoginComponent}
      loginComponentProps={undefined}
      loginProps={props}
    />
  );
});

const RedoLoginComponent = memo(function RedoLoginComponent({
  emailEntered,
  promptParams,
  fourDigitCodeId,
  textParams,
  handleFourDigitCodeSubmit,
  fourDigitCodeInput,
  fourDigitCodeSubmit,
  doFourDigitCodeSubmit,
  submitButtonParams,
  secondsSinceLastSent,
  emailSubmit,
  handleEmailSubmit,
  handleChangeEmail,
  showIncorrectCodeError,
  showMarketingCheckbox,
  emailInput,
  marketingAccepted,
  setMarketingAccepted,
  subscribeToMarketingText,
}: RedoLoginComponentProps<undefined>) {
  return (
    <>
      {!emailEntered && promptParams && (
        <>
          <Header fontFamily="inherit" fontSize="xs">
            {promptParams.promptText}
          </Header>
          {promptParams.promptSubtext && (
            <Text fontSize="sm">{promptParams.promptSubtext}</Text>
          )}
        </>
      )}
      <Flex dir="column" gap="xl">
        {!!emailEntered && fourDigitCodeId ? (
          <>
            <Text
              fontSize={textParams?.fontSize}
              fontWeight={textParams?.fontWeight}
            >
              We sent a 4-digit code to <strong>{emailEntered}</strong>. Please
              enter the code below.
            </Text>
            <Flex
              as="form"
              dir="column"
              gap="xl"
              onSubmit={handleFourDigitCodeSubmit}
            >
              <OtpInput
                numeric
                onChange={(value) => {
                  fourDigitCodeInput.setValue(value);
                  if (value.join("").length === 4) {
                    doFourDigitCodeSubmit();
                  }
                }}
                values={fourDigitCodeInput.value}
              />
              <Button
                border={ButtonBorder.TRANSPARENT}
                disabled={
                  fourDigitCodeInput.errors.length > 0 ||
                  !fourDigitCodeInput.value ||
                  fourDigitCodeSubmit.pending
                }
                pending={fourDigitCodeSubmit.pending}
                size={submitButtonParams?.size ?? ButtonSize.SMALL}
                theme={ButtonTheme.PRIMARY}
                type="submit"
              >
                <Text
                  fontSize={textParams?.fontSize}
                  fontWeight={textParams?.fontWeight}
                >
                  Submit
                </Text>
              </Button>
              {(secondsSinceLastSent === undefined ||
                secondsSinceLastSent > RESEND_CODE_DELAY) &&
              !emailSubmit.pending ? (
                <ExternalLink
                  className={redoLoginCss.secondaryOption}
                  onClick={handleEmailSubmit}
                  showIcon={false}
                >
                  <Text
                    fontSize={textParams?.fontSize}
                    fontWeight={textParams?.fontWeight}
                  >
                    Send another code
                  </Text>
                </ExternalLink>
              ) : (
                <Text
                  className={redoLoginCss.secondaryOption}
                  fontSize={textParams?.fontSize}
                  fontWeight={textParams?.fontWeight}
                  textColor="disabled"
                >
                  Send another code
                  {!emailSubmit.pending &&
                    secondsSinceLastSent !== undefined && (
                      <> ({RESEND_CODE_DELAY - secondsSinceLastSent + 1})</>
                    )}
                </Text>
              )}
              <ExternalLink
                className={redoLoginCss.secondaryOption}
                onClick={handleChangeEmail}
                showIcon={false}
              >
                <Text
                  fontSize={textParams?.fontSize}
                  fontWeight={textParams?.fontWeight}
                >
                  Change email
                </Text>
              </ExternalLink>
            </Flex>
            {showIncorrectCodeError && (
              <Text
                fontSize={textParams?.fontSize}
                fontWeight={textParams?.fontWeight}
                textColor="error"
              >
                The code you entered is incorrect. Please check your email for
                another code and try again.
              </Text>
            )}
          </>
        ) : (
          <Flex as="form" dir="column" gap="xl" onSubmit={handleEmailSubmit}>
            <FormTextInput
              input={emailInput}
              label="Email"
              placeholder="Enter your email"
              size={InputSize.MEDIUM}
            />
            {showMarketingCheckbox && (
              <Checkbox
                onChange={(checked) => setMarketingAccepted(checked)}
                value={marketingAccepted}
              >
                {subscribeToMarketingText}
              </Checkbox>
            )}
            <Button
              border={ButtonBorder.TRANSPARENT}
              disabled={
                emailInput.errors.length > 0 ||
                !emailInput.value ||
                emailSubmit.pending
              }
              pending={emailSubmit.pending}
              size={submitButtonParams?.size ?? ButtonSize.SMALL}
              theme={ButtonTheme.PRIMARY}
              type="submit"
            >
              <Text
                fontSize={textParams?.fontSize}
                fontWeight={textParams?.fontWeight}
              >
                {submitButtonParams?.sendOneTimePasscodeButtonText ??
                  "Send one-time passcode"}
              </Text>
            </Button>
          </Flex>
        )}
      </Flex>
    </>
  );
});
